/**
 * Authentication Client
 *
 * Configures the better-auth React client for session management.
 * Connects to our Hono API backend for auth endpoints.
 *
 * Exports:
 * - useSession: Hook for accessing current user session
 * - signIn/signOut/signUp: Auth actions
 * - resetPassword/forgotPassword: Password recovery
 */
/// <reference types="vite/client" />
import { createAuthClient } from 'better-auth/react';
import { adminClient } from 'better-auth/client/plugins';

export const authClient = createAuthClient({
  baseURL: (import.meta.env.VITE_API_URL as string | undefined) ?? '',
  plugins: [adminClient()],
  fetchOptions: {
    credentials: 'include', // Required for cookies to be sent/received
  },
});

export const { useSession, signIn, signOut, signUp, resetPassword, changePassword, updateUser } =
  authClient;

// Forgot password - sends reset email
export const forgotPassword = (email: string) => {
  // Use requestPasswordReset (v1.x) or forgetPassword (v0.x) depending on version
  type PasswordResetMethod = (opts: { email: string; redirectTo: string }) => Promise<unknown>;
  const client = authClient as {
    requestPasswordReset?: PasswordResetMethod;
    forgetPassword?: PasswordResetMethod;
  };
  const method = client.requestPasswordReset ?? client.forgetPassword;
  if (method) {
    return method({ email, redirectTo: '/reset-password' });
  }
  throw new Error('Password reset method not available');
};
